import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { useConfirm } from "./ConfirmDialog";

async function safe(fn) { try { return await fn(); } catch { return null; } }

function fmtDT(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("es-EC",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit",hour12:false}); }
  catch { return d; }
}

function humanize(k) {
  return String(k).replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
}

// "POST /v1/onboarding/api/register" → { method:"POST", path:"/v1/onboarding/api/register" }
function parseEp(str) {
  const m = String(str||"").trim().match(/^(\S+)\s+([\s\S]*)$/);
  return m ? { method:m[1].toUpperCase(), path:m[2].trim() } : { method:"", path:String(str||"") };
}

// Fallback espejo de GET /v1/partners/docs/openapi-info (por si el fetch falla).
// La fuente de verdad es el backend; esto solo evita una pantalla vacía.
const FALLBACK_API = {
  version: "v1",
  base_url: "/v1",
  auth: "X-API-Key header",
  docs: "/docs",
  sandbox: true,
  endpoints: {
    register_client:   "POST /v1/onboarding/api/register",
    sign_contract:     "POST /v1/contracts/sign-api",
    list_clients:      "GET /v1/onboarding/agent/list",
    client_status:     "GET /v1/onboarding/status/{client_id}",
    edit_did:          "PATCH /v1/did/client/{client_id}",
    activation_status: "GET /v1/activation/status/{client_id}",
    did_provinces:     "GET /v1/activation/did/provinces",
  },
  webhooks: {
    client_activated: "POST {webhook_url} body: {event, client_id, sip_user, did_number}",
    did_changed:      "POST {webhook_url} body: {event, client_id, old_did, new_did}",
    payment_approved: "POST {webhook_url} body: {event, client_id, amount, ref}",
  },
};

function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = {success:"nv-alert-ok",error:"nv-alert-err",warn:"nv-alert-warn"}[msg.type]||"nv-alert-ok";
  return (
    <div className={"nv-alert "+cls} style={{ marginBottom:14 }}>
      <span style={{ flex:1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background:"none",border:"none",cursor:"pointer",color:"inherit",fontSize:14 }}>✕</button>
    </div>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); });
    }
  };
  return (
    <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={copy} style={{ flexShrink:0 }}>
      {copied?"✓ Copiado":"Copiar"}
    </button>
  );
}

const MC = { GET:"var(--success)", POST:"var(--brand)", PUT:"var(--warning)", PATCH:"var(--warning)", DELETE:"var(--danger)" };

function MethodBadge({ method }) {
  return (
    <span style={{ fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,
      background:(MC[method]||"var(--text-muted)")+"22",color:MC[method]||"var(--text-muted)",
      fontFamily:"var(--font-mono)" }}>{method||"—"}</span>
  );
}

export default function APICenter() {
  const [partners, setPartners] = useState([]);
  const [apiInfo,  setApiInfo]  = useState(FALLBACK_API);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState("partners");
  const [msg,      setMsg]      = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [secret,   setSecret]   = useState(null); // { api_key, user_password? } — se muestra una sola vez
  const [form,     setForm]     = useState({ name:"", contact_email:"", webhook_url:"" });
  const [busy,     setBusy]     = useState(false);
  const [formErr,  setFormErr]  = useState(null);
  // window.confirm no abre cartel en el navegador de Carlos y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();

  const showMsg = (text,type="success") => { setMsg({text,type}); setTimeout(()=>setMsg(null),5000); };
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const load = useCallback(async () => {
    setLoading(true);
    const [rp, ri] = await Promise.all([
      safe(()=>api.get("/v1/partners")),
      safe(()=>api.get("/v1/partners/docs/openapi-info")),
    ]);
    // /v1/partners responde { total, data:[...] }. Extraer SIEMPRE un array
    // (si no, partners queda como objeto y partners.filter(...) revienta el render).
    const body = rp?.data;
    const list = Array.isArray(body) ? body : (body?.data || body?.partners || []);
    setPartners(Array.isArray(list) ? list : []);
    if (ri?.data && ri.data.endpoints) setApiInfo(ri.data);
    setLoading(false);
  },[]);

  useEffect(()=>{ load(); },[load]);

  const handleCreate = async () => {
    if (!form.name||!form.contact_email) return setFormErr("Nombre y email son requeridos");
    setBusy(true); setFormErr(null);
    try {
      const r = await api.post("/v1/partners", form);
      setShowCreate(false);
      setForm({ name:"", contact_email:"", webhook_url:"" });
      // El backend genera API key + un usuario de portal (password). Se muestran una sola vez.
      if (r.data?.api_key) setSecret({ api_key:r.data.api_key, user_password:r.data.user_password });
      showMsg("Partner creado");
      load();
    } catch(e) { setFormErr(e?.response?.data?.detail||"Error al crear"); }
    finally { setBusy(false); }
  };

  const handleRegen = async (id) => {
    if (!(await confirmar("Rotar la API key invalida la anterior. Continuar?"))) return;
    try {
      const r = await api.post("/v1/partners/"+id+"/rotate-key");
      if (r.data?.api_key) setSecret({ api_key:r.data.api_key });
      showMsg("API key rotada");
    } catch(e) { showMsg(e?.response?.data?.detail||"Error","error"); }
  };

  const handleToggle = async (p) => {
    try {
      const status = p.status==="active"?"suspended":"active";
      await api.patch("/v1/partners/"+p.id,{ status });
      showMsg("Partner "+(status==="active"?"activado":"suspendido"));
      load();
    } catch(e) { showMsg(e?.response?.data?.detail||"Error","error"); }
  };

  const activos = partners.filter(p=>p.status==="active").length;
  const info = apiInfo || FALLBACK_API;
  const endpointList = Object.entries(info.endpoints||{}).map(([k,v])=>({ ...parseEp(v), label:humanize(k) }));
  const webhookList  = Object.entries(info.webhooks||{}).map(([k,v])=>({ event:k, raw:String(v) }));
  const ORIGIN = (typeof window!=="undefined" && window.location && window.location.origin) || "https://mi.eneural.org";

  return (
    <div>
      {dialogoConfirm}
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">API Center</div>
          <div className="nv-page-sub">Gestión de partners e integración (API pública v1 · auth por API key)</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={load} disabled={loading}>
            {loading?<span className="nv-spinner"/>:"↺"} Actualizar
          </button>
          <button className="nv-btn nv-btn-primary" onClick={()=>setShowCreate(true)}>+ Nuevo partner</button>
        </div>
      </div>

      <div className="nv-kpi-grid" style={{ marginBottom:16 }}>
        <div className="nv-kpi"><div className="nv-kpi-label">Total partners</div><div className="nv-kpi-value" style={{ color:"var(--brand)" }}>{partners.length}</div></div>
        <div className="nv-kpi"><div className="nv-kpi-label">Activos</div><div className="nv-kpi-value" style={{ color:"var(--success)" }}>{activos}</div></div>
        <div className="nv-kpi"><div className="nv-kpi-label">Suspendidos</div><div className="nv-kpi-value" style={{ color:"var(--text-muted)" }}>{partners.length-activos}</div></div>
        <div className="nv-kpi"><div className="nv-kpi-label">Endpoints API</div><div className="nv-kpi-value" style={{ color:"var(--info)" }}>{endpointList.length}</div></div>
      </div>

      <Alert msg={msg} onClose={()=>setMsg(null)}/>

      <div className="nv-tabs">
        {[["partners","Partners"],["docs","Docs API"],["info","Integración"]].map(([k,l])=>(
          <div key={k} className={"nv-tab"+(tab===k?" active":"")} onClick={()=>setTab(k)}>{l}</div>
        ))}
      </div>

      {tab==="partners"&&(
        loading?<div className="nv-loading"><span className="nv-spinner"/></div>:
        partners.length===0?(
          <div className="nv-card" style={{ textAlign:"center",padding:"48px 20px" }}>
            <div style={{ fontSize:32,opacity:.2,marginBottom:12 }}>⊕</div>
            <div style={{ fontSize:13,color:"var(--text-muted)",marginBottom:16 }}>Sin partners registrados</div>
            <button className="nv-btn nv-btn-primary" onClick={()=>setShowCreate(true)}>+ Crear primer partner</button>
          </div>
        ):(
          <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
            {partners.map(p=>(
              <div key={p.id} className="nv-card">
                <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:14,fontWeight:700,color:"var(--text-primary)" }}>{p.name}</div>
                    <div style={{ fontSize:11,color:"var(--text-muted)" }}>
                      Alta {fmtDT(p.created_at)} · {p.total_clients||0} cliente{(p.total_clients||0)===1?"":"s"}
                    </div>
                  </div>
                  <span className={"nv-badge "+(p.status==="active"?"nv-badge-ok":"nv-badge-muted")}>
                    {p.status==="active"?"Activo":"Suspendido"}
                  </span>
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
                  <div style={{ flex:1,background:"var(--bg-raised)",borderRadius:"var(--r-sm)",padding:"7px 12px",
                    fontFamily:"var(--font-mono)",fontSize:11,color:"var(--text-muted)",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}
                    title="La API key se guarda cifrada y solo se muestra al crear o rotar">
                    🔒 API key oculta · visible solo al crear/rotar
                  </div>
                  <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={()=>handleRegen(p.id)}>↻ Rotar key</button>
                  <button className="nv-btn nv-btn-sm" onClick={()=>handleToggle(p)}
                    style={{ background:p.status==="active"?"var(--warning-bg)":"var(--success-bg)",
                      color:p.status==="active"?"var(--warning)":"var(--success)",
                      border:"none",borderRadius:"var(--r-sm)",padding:"4px 10px",fontSize:11,cursor:"pointer" }}>
                    {p.status==="active"?"⏸ Suspender":"▶ Activar"}
                  </button>
                </div>
                {p.webhook_url&&(
                  <div style={{ fontSize:10,color:"var(--text-muted)",fontFamily:"var(--font-mono)" }}>
                    Webhook: {p.webhook_url}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {tab==="docs"&&(
        <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
          <div className="nv-card">
            <div className="nv-card-header">
              <span className="nv-card-title">▦ API pública de partners — {info.version||"v1"}</span>
              <a href={info.docs||"/docs"} target="_blank" rel="noreferrer" className="nv-btn nv-btn-ghost nv-btn-sm">Swagger ↗</a>
            </div>
            <div style={{ fontSize:12,color:"var(--text-secondary)",lineHeight:1.7,marginBottom:12 }}>
              Estos son los endpoints que un integrador consume con su <b>API key</b> (header <code>{info.auth||"X-API-Key"}</code>).
              El alta/gestión de partners es administrativa (sesión de panel), no parte de la API pública.
            </div>
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead><tr><th>Acción</th><th>Método</th><th>Endpoint</th></tr></thead>
                <tbody>
                  {endpointList.map((e,i)=>(
                    <tr key={i}>
                      <td style={{ fontSize:12,color:"var(--text-secondary)" }}>{e.label}</td>
                      <td><MethodBadge method={e.method}/></td>
                      <td style={{ fontFamily:"var(--font-mono)",fontSize:11,color:"var(--brand)" }}>{e.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {webhookList.length>0&&(
            <div className="nv-card">
              <div className="nv-card-header"><span className="nv-card-title">⇄ Webhooks (eventos salientes)</span></div>
              <div style={{ fontSize:12,color:"var(--text-secondary)",lineHeight:1.7,marginBottom:12 }}>
                Netvoice hace POST a tu <code>webhook_url</code> cuando ocurren estos eventos. Probalo con
                <code> POST /v1/partners/{"{id}"}/webhook-test</code>.
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {webhookList.map((w,i)=>(
                  <div key={i} style={{ background:"var(--bg-raised)",borderRadius:"var(--r-sm)",padding:"8px 12px" }}>
                    <div style={{ fontSize:11,fontWeight:700,color:"var(--text-primary)",marginBottom:3 }}>{humanize(w.event)}</div>
                    <div style={{ fontFamily:"var(--font-mono)",fontSize:10.5,color:"var(--text-muted)",wordBreak:"break-word" }}>{w.raw}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab==="info"&&(
        <div className="nv-grid-2">
          <div className="nv-card">
            <div className="nv-card-header"><span className="nv-card-title">Autenticación</span></div>
            <div style={{ fontSize:12,color:"var(--text-secondary)",lineHeight:1.8,marginBottom:12 }}>
              Cada request a la API pública lleva la API key del partner en el header:
            </div>
            <div style={{ background:"var(--bg-raised)",borderRadius:"var(--r-sm)",padding:"12px",marginBottom:12 }}>
              <div style={{ fontFamily:"var(--font-mono)",fontSize:11,color:"var(--success)",lineHeight:1.8 }}>
                X-API-Key: nv_tu_api_key_aqui
              </div>
            </div>
            <div style={{ fontSize:11,color:"var(--text-muted)",lineHeight:1.7 }}>
              La key se genera al crear el partner y se rota desde la pestaña Partners. Se guarda
              cifrada: si se pierde, hay que rotarla. {info.sandbox?"Hay entorno sandbox disponible.":""}
            </div>
          </div>
          <div className="nv-card">
            <div className="nv-card-header"><span className="nv-card-title">URLs base</span></div>
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              {[
                ["Producción", ORIGIN],
                ["API v1",     ORIGIN + (info.base_url||"/v1")],
                ["Swagger",    ORIGIN + (info.docs||"/docs")],
                ["Health",     ORIGIN + "/noc/health"],
              ].map(([lbl,url])=>(
                <div key={lbl} style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"var(--bg-raised)",borderRadius:"var(--r-sm)" }}>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:9,textTransform:"uppercase",color:"var(--text-muted)",marginBottom:1 }}>{lbl}</div>
                    <div style={{ fontSize:11,fontFamily:"var(--font-mono)",color:"var(--brand)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{url}</div>
                  </div>
                  <CopyBtn value={url}/>
                </div>
              ))}
            </div>
            <div style={{ fontSize:10,color:"var(--text-muted)",marginTop:10,lineHeight:1.6 }}>
              Arquitectura consolidada: el panel y la API se sirven desde el mismo host
              (<code>{ORIGIN.replace(/^https?:\/\//,"")}</code>) sobre TLS, vía nginx → panel-api.
            </div>
          </div>
        </div>
      )}

      {showCreate&&(
        <div className="nv-modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowCreate(false)}>
          <div className="nv-modal">
            <div className="nv-modal-header">
              <span className="nv-modal-title">Nuevo partner API</span>
              <button className="nv-modal-close" onClick={()=>setShowCreate(false)}>✕</button>
            </div>
            {formErr&&<div className="nv-alert nv-alert-err" style={{ marginBottom:14 }}>{formErr}</div>}
            <div className="nv-form-row">
              <div className="nv-form-field">
                <label className="nv-label">Nombre *</label>
                <input className="nv-input" value={form.name} onChange={e=>setF("name",e.target.value)} placeholder="ej: Integrador CRM"/>
              </div>
              <div className="nv-form-field">
                <label className="nv-label">Email *</label>
                <input className="nv-input" type="email" value={form.contact_email} onChange={e=>setF("contact_email",e.target.value)} placeholder="api@empresa.com"/>
              </div>
            </div>
            <div className="nv-form-field" style={{ marginBottom:16 }}>
              <label className="nv-label">Webhook URL</label>
              <input className="nv-input" value={form.webhook_url} onChange={e=>setF("webhook_url",e.target.value)} placeholder="https://miempresa.com/webhook" style={{ fontFamily:"var(--font-mono)" }}/>
            </div>
            <div className="nv-modal-footer">
              <button className="nv-btn nv-btn-ghost" onClick={()=>setShowCreate(false)}>Cancelar</button>
              <button className="nv-btn nv-btn-primary" onClick={handleCreate} disabled={busy}>
                {busy?<span className="nv-spinner"/>:"+ Crear partner"}
              </button>
            </div>
          </div>
        </div>
      )}

      {secret&&(
        <div className="nv-modal-overlay">
          <div className="nv-modal">
            <div className="nv-modal-header"><span className="nv-modal-title">Credenciales generadas</span></div>
            <div className="nv-alert nv-alert-warn" style={{ marginBottom:14 }}>
              Guarda esto ahora. No se mostrará nuevamente.
            </div>
            <div style={{ fontSize:10,textTransform:"uppercase",color:"var(--text-muted)",marginBottom:4 }}>API key (header X-API-Key)</div>
            <div style={{ display:"flex",gap:8,alignItems:"center",marginBottom:secret.user_password?14:0 }}>
              <div style={{ flex:1,background:"var(--bg-raised)",borderRadius:"var(--r-sm)",padding:"12px" }}>
                <div style={{ fontFamily:"var(--font-mono)",fontSize:12,color:"var(--success)",wordBreak:"break-all" }}>{secret.api_key}</div>
              </div>
              <CopyBtn value={secret.api_key}/>
            </div>
            {secret.user_password&&(<>
              <div style={{ fontSize:10,textTransform:"uppercase",color:"var(--text-muted)",marginBottom:4 }}>Contraseña del usuario de portal</div>
              <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                <div style={{ flex:1,background:"var(--bg-raised)",borderRadius:"var(--r-sm)",padding:"12px" }}>
                  <div style={{ fontFamily:"var(--font-mono)",fontSize:12,color:"var(--brand)",wordBreak:"break-all" }}>{secret.user_password}</div>
                </div>
                <CopyBtn value={secret.user_password}/>
              </div>
            </>)}
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
              <button className="nv-btn nv-btn-primary" onClick={()=>setSecret(null)}>✓ Entendido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
